import { DS } from '../../design-system'
import { useStore } from '../../models'
import MyFirstGameInterstitial from './MyFirstGameInterstitial'
import { fontFamily } from '../../theme'
import DiamondBulletPoint from '../../../assets/MyFirstGame/diamond-bullet-point.svg'
import { observer } from 'mobx-react-lite'
import { Card } from '../../components/ANewDesign/Card'
import { ToolTip } from './MyFirstGameSingleTooltip'
import React, { useEffect, useRef } from 'react'
import { Animated, TouchableOpacity, StyleSheet, Platform, useWindowDimensions, View, ViewStyle } from 'react-native'
import { LinearGradient } from 'expo-linear-gradient'
import { Portal } from '@gorhom/portal'

import MyFirstGameStepNames from '../../models/game/first-game/MyFirstGameStepNames'
import { stagesWithNextStepFloatingButton } from '../../models/game/first-game/MyFirstGameUtils'
import { useResponsive } from '@/screens/new-design/hooks'
import { LayoutShiftContent } from '@/hooks/useLayoutShiftRegistry'
import { useBreakPointValue } from '@/components/WebWrapper'

const isWeb = Platform.OS === 'web'

export type ContentLayout = {
  x: number
  y: number
  width: number
  height: number
  pageX: number
  pageY: number
}
type MyFirstGameOverlayProps = {
  contentLayout?: ContentLayout
}

const MyFirstGameOverlay = observer(({ contentLayout }: MyFirstGameOverlayProps) => {
  // const overlayHeight = contentLayout?.height
  const windowDimensions = useWindowDimensions()
  const { width } = windowDimensions
  const isSmallDevice = width < 380
  const { verticalScale } = useResponsive()

  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        container: { zIndex: 5, elevation: 40, width, position: 'absolute' },
        floatingButtonContainer: {
          position: 'absolute',
          zIndex: 9,
          bottom: 160,
          // bottom: 16,
          right: 16
        },
        floatingButton: {
          width: 56,
          height: 56,
          borderRadius: 56,
          backgroundColor: '#6713D3',
          elevation: 10,
          shadowColor: 'black',
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.5,
          shadowRadius: 3
        },
        floatingButtonGradient: {
          width: 56,
          height: 56,
          paddingHorizontal: 16,
          paddingVertical: 12,
          borderRadius: 56,
          borderWidth: 2,
          borderColor: '#822852',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center'
        }
      }),
    [width]
  )

  const webStyles = React.useMemo(
    () =>
      StyleSheet.create({
        container: {
          zIndex: 5,
          elevation: 40,
          width: '100%',
          height: '80%',
          position: 'absolute',
          flexDirection: 'column',
          flexGrow: 1,
          left: 0
        },
        floatingButtonContainer: {
          position: 'absolute',
          bottom: 16,
          right: 16
        },
        floatingButton: styles.floatingButton,
        floatingButtonGradient: styles.floatingButtonGradient,
        tooltipContainerStyle: {
          maxWidth: '80%'
        },
        tooltipStyle: {
          display: 'flex',
          flexDirection: 'row',
          flexShrink: 1,
          flexWrap: 'wrap',
          maxWidth: '100%'
        }
      }),
    [styles.floatingButton, styles.floatingButtonGradient]
  )

  const {
    game: { stageInternalName, goToNextFirstGameStep, communityCards }
  } = useStore()

  function getResponsiveLeftHint(width: number): string {
    if (width < 992) return '10%'
    if (width < 1280) return '26%'
    if (width < 1536) return '46%'
    return '55%'
  }
  function getResponsiveLeftBot(width: number): string {
    if (width < 992) return '5%'
    if (width < 1280) return '16%'
    if (width < 1536) return '32%'
    return '46%'
  }

  const NextFloatingButton = () => {
    const opacity = useRef(new Animated.Value(0)).current

    useEffect(() => {
      Animated.timing(opacity, {
        toValue: 1,
        duration: 150,
        useNativeDriver: true
      }).start()
    }, [opacity])

    return (
      <Animated.View
        style={[
          { opacity },
          Platform.OS === 'web' ? webStyles.floatingButtonContainer : styles.floatingButtonContainer
        ]}
      >
        <TouchableOpacity
          style={styles.floatingButton}
          onPress={() => {
            goToNextFirstGameStep()
          }}
        >
          <LinearGradient colors={['#6713D3', '#480D93']} locations={[0, 1]} style={styles.floatingButtonGradient}>
            <DS.Icon name={'Arrow---right'} size={24} color={'Global/White'} />
          </LinearGradient>
        </TouchableOpacity>
      </Animated.View>
    )
  }

  return (
    <>
      {stagesWithNextStepFloatingButton.includes(stageInternalName) && (
        <View
          style={{
            position: 'absolute',
            zIndex: 9,
            bottom: isWeb ? undefined : 0,
            top: isWeb ? (contentLayout?.height || 0) + (contentLayout?.pageY || 0) : undefined,
            right: 0
          }}
        >
          <NextFloatingButton />
        </View>
      )}
      <OverlayForStage stage={stageInternalName} contentLayout={contentLayout} />
      <TooltipForStage stage={stageInternalName} contentLayout={contentLayout} />
    </>
  )
})

const OverlayForStage = observer(
  ({
    stage,
    contentLayout
  }: {
    stage: MyFirstGameStepNames
    contentLayout: MyFirstGameOverlayProps['contentLayout']
  }) => {
    const store = useStore()
    const windowDimensions = useWindowDimensions()
    const Component = OverlayStageMap[stage]
    if (!Component) {
      return null
    }
    return (
      <View
        style={[
          {
            zIndex: 6,
            position: 'absolute',
            marginTop: contentLayout?.pageY,
            height: contentLayout?.height
          }
        ]}
      >
        <Component contentLayout={contentLayout} windowDimensions={windowDimensions} store={store} />
      </View>
    )
  }
)

const TooltipForStage = observer(
  ({
    stage,
    contentLayout
  }: {
    stage: MyFirstGameStepNames
    contentLayout: MyFirstGameOverlayProps['contentLayout']
  }) => {
    const store = useStore()
    const windowDimensions = useWindowDimensions()
    const Component = ToolTipStageMap[stage]
    if (!Component) {
      return null
    }
    return <Component contentLayout={contentLayout} windowDimensions={windowDimensions} store={store} />
  }
)

type MyFirstGameStepString = keyof typeof MyFirstGameStepNames

type ToolTipComponentProps = {
  contentLayout: MyFirstGameOverlayProps['contentLayout']
  windowDimensions: ReturnType<typeof useWindowDimensions>
  store: ReturnType<typeof useStore>
}
type ToolTipComponent = (props: ToolTipComponentProps) => React.ReactElement

function getTranslateXCentered(
  width: number,
  {
    contentLayout,
    windowDimensions
  }: {
    contentLayout: MyFirstGameOverlayProps['contentLayout']
    windowDimensions: ReturnType<typeof useWindowDimensions>
  }
) {
  const contentWidth = contentLayout?.width ? contentLayout?.width / 2 : 0
  return isWeb ? contentWidth - width / 2 : windowDimensions.width / 2 - width / 2
}

const ToolTipStageMap: Record<MyFirstGameStepString[number], ToolTipComponent> = {
  [MyFirstGameStepNames.holeAndCommunityCards]: ({ contentLayout, windowDimensions }) => (
    <>
      <LayoutShiftContent hostName="myFirstGame.board.cards" position="top" offset={{ left: 0, right: 0 }}>
        <ToolTip
          title={'Community Cards'}
          showNext={false}
          showArrow={true}
          containerStyle={{
            marginHorizontal: 'auto',
            marginTop: -64
          }}
        />
      </LayoutShiftContent>
      <LayoutShiftContent hostName="myFirstGame.playerCards" position="top" offset={{ left: 0, right: 0 }}>
        <ToolTip
          title={'Your Hole Cards'}
          showNext={false}
          showArrow={true}
          containerStyle={{
            marginHorizontal: 'auto',
            marginTop: -32
          }}
        />
      </LayoutShiftContent>
    </>
  ),
  [MyFirstGameStepNames.letsGoFaceUp]: ({ contentLayout, windowDimensions }) => {
    const marginHorizontal = useBreakPointValue<ViewStyle['marginHorizontal']>({
      default: 32,
      sm: 'auto'
    })
    return (
      <LayoutShiftContent hostName="myFirstGame.board" position="bottom" offset={{ left: 0, right: 0 }}>
        <ToolTip
          title={'Let’s go face-up'}
          description={'Your opponents’ cards will be played face-up this hand to help you as you learn the game.'}
          showArrow={false}
          containerStyle={{
            zIndex: 999,
            marginTop: -60,
            marginHorizontal: marginHorizontal
          }}
        />
      </LayoutShiftContent>
    )
  },
  [MyFirstGameStepNames.faceUpNote]: ({ contentLayout, windowDimensions }) => {
    const marginHorizontal = useBreakPointValue<ViewStyle['marginHorizontal']>({
      default: 32,
      sm: 'auto'
    })
    const marginTop = useBreakPointValue<ViewStyle['marginTop']>({
      default: -110,
      xs: -90,
      sm: -100
    })
    const maxWidth = useBreakPointValue<ViewStyle['maxWidth']>({
      default: '100%',
      sm: 440
    })
    return (
      <LayoutShiftContent hostName="myFirstGame.playerCards" position="top" offset={{ left: 0, right: 0 }}>
        <ToolTip
          title={'Note'}
          description={
            "In regular games, hole cards are kept private, so you typically won’t reveal yours or see your opponents' cards until the end."
          }
          showArrow={true}
          containerStyle={{
            zIndex: 999,
            marginTop,
            marginHorizontal,
            maxWidth
          }}
        />
      </LayoutShiftContent>
    )
  },
  [MyFirstGameStepNames.roles]: () => {
    const marginLeftDealer = useBreakPointValue<ViewStyle['marginLeft']>({
      default: 16,
      sm: 'auto'
    })
    return (
      <>
        <LayoutShiftContent hostName="myFirstGame.playerCell-Esther.chips" position="bottom-left" offset={{ left: 0 }}>
          <ToolTip
            title={'Big Blind'}
            showNext={false}
            showArrow={true}
            arrowPosition={'left'}
            containerStyle={{
              marginTop: -40,
              transform: [{ translateX: 120 }]
            }}
          />
        </LayoutShiftContent>
        <LayoutShiftContent hostName="myFirstGame.playerCell-Claudia.chips" position="bottom-left" offset={{ left: 0 }}>
          <ToolTip
            title={'Small Blind'}
            showNext={false}
            showArrow={true}
            arrowPosition={'left'}
            containerStyle={{
              marginTop: -40,
              transform: [{ translateX: 120 }]
            }}
          />
        </LayoutShiftContent>
        <LayoutShiftContent hostName="myFirstGame.playerHand" position="top">
          <ToolTip
            title={'Dealer'}
            showNext={false}
            showArrow={true}
            arrowPosition={'bottomLeft'}
            containerStyle={{
              marginTop: -84,
              marginLeft: marginLeftDealer
            }}
          />
        </LayoutShiftContent>
      </>
    )
  },
  [MyFirstGameStepNames.winnerNote]: ({ contentLayout, windowDimensions }) => (
    <LayoutShiftContent hostName="myFirstGame.playerCards" position="top">
      <ToolTip
        title={'Winner!'}
        description={
          'Congratulations! You won with three of a kind, Jacks! Although Petra had the lead until the final card was revealed, the last Jack turned the game in your favor.'
        }
        showArrow={true}
        containerStyle={{
          zIndex: 999,
          marginTop: -150,
          maxWidth: '100%',
          width: 330,
          transform: [
            {
              translateX: getTranslateXCentered(330, { contentLayout, windowDimensions })
            }
          ]
        }}
      />
    </LayoutShiftContent>
  ),
  [MyFirstGameStepNames.forcedBets]: ({ contentLayout, windowDimensions }) => {
    const marginHorizontal = useBreakPointValue<ViewStyle['marginHorizontal']>({
      default: 32,
      sm: 'auto'
    })
    const marginTop = useBreakPointValue<ViewStyle['marginTop']>({
      default: -90,
      sm: -45
    })

    return (
      <LayoutShiftContent hostName="myFirstGame.board" position="bottom" offset={{ left: 0, right: 0 }}>
        <ToolTip
          title={'Forced Bets'}
          description={
            'SB and BB must place forced bets to encourage action in the game. After each hand completes, the player positions rotate one position clockwise.'
          }
          containerStyle={{
            zIndex: 999,
            marginHorizontal: marginHorizontal,
            marginTop: marginTop
          }}
        />
      </LayoutShiftContent>
    )
  },
  [MyFirstGameStepNames.preFlopStrategy]: ({ contentLayout, windowDimensions }) => {
    const width = useBreakPointValue({
      default: 330,
      sm: undefined
    }) as number | undefined
    return (
      <LayoutShiftContent hostName="myFirstGame.board" position="bottom">
        <ToolTip
          title={'Pre-Flop Round Strategy'}
          description={
            'In the pre-flop round, if no one has raised the big blind, we suggest raising if your hand is worth playing. Calling the big blind, known as Limping In, is a conservative play we advise against. If someone has raised, you’re free to call their raise.'
          }
          containerStyle={{
            zIndex: 999,
            marginTop: !width ? -70 : -140,
            maxWidth: '100%',
            width: width,
            transform: [
              {
                translateX: !width ? 0 : getTranslateXCentered(330, { contentLayout, windowDimensions })
              }
            ]
          }}
        />
      </LayoutShiftContent>
    )
  },
  [MyFirstGameStepNames.userTurnCall1]: ({ contentLayout, windowDimensions }) => (
    <LayoutShiftContent hostName="myFirstGame.playerCards" position="top">
      <ToolTip
        title={'Call'}
        description={
          'You have a pair, Jack of hearts and Jack of Diamonds. To stay in the hand and keep playing, select Call to match Petras raise of 300 chips.'
        }
        showArrow={true}
        showNext={false}
        containerStyle={{
          zIndex: 999,
          marginTop: -130,
          maxWidth: '100%',
          width: 330,
          transform: [
            {
              translateX: getTranslateXCentered(330, { contentLayout, windowDimensions })
            }
          ]
        }}
      />
    </LayoutShiftContent>
  ),
  [MyFirstGameStepNames.claudiaFolds]: ({ contentLayout, windowDimensions }) => (
    <LayoutShiftContent hostName="myFirstGame.playerCell-Claudia" position="bottom">
      <ToolTip
        title={'Folds'}
        description={
          'Claudia folded, believing their cards weren’t strong enough to call your bet. They’re out for this hand.'
        }
        showArrow={true}
        arrowPosition={'topLeft'}
        containerStyle={{
          maxWidth: '100%',
          width: 330,
          marginTop: 16,
          marginLeft: 12
        }}
      />
    </LayoutShiftContent>
  ),
  [MyFirstGameStepNames.estherPreCheck]: ({ contentLayout, windowDimensions }) => {
    const width = useBreakPointValue<ViewStyle['width']>({
      default: 330,
      sm: undefined
    })

    return (
      <LayoutShiftContent hostName="myFirstGame.board" position="bottom">
        <ToolTip
          title={'Check'}
          description={
            'After the flop, since no one has bet yet, Esther can either bet or check. "Checking" lets her pass the action to the next player, gaining insight into their hand strength. By checking, she stays in the hand without adding more chips unless another player raises.'
          }
          containerStyle={{
            zIndex: 999,
            marginTop: !width ? -70 : -150,
            maxWidth: '100%',
            width: width,
            transform: [
              {
                translateX: !width ? 0 : getTranslateXCentered(330, { contentLayout, windowDimensions })
              }
            ]
          }}
        />
      </LayoutShiftContent>
    )
  },
  [MyFirstGameStepNames.userTurnCall2]: ({ contentLayout, windowDimensions }) => (
    <LayoutShiftContent hostName="myFirstGame.playerCards" position="top">
      <ToolTip
        title={'Call'}
        description={
          "After the flop, your hand strength remains the same, with your pair of Jacks still as your best hand. Select Call to match Petra's bet and stay in the game."
        }
        showArrow={true}
        showNext={false}
        containerStyle={{
          zIndex: 999,
          marginTop: -130,
          maxWidth: '100%',
          width: 330,
          transform: [
            {
              translateX: getTranslateXCentered(330, { contentLayout, windowDimensions })
            }
          ]
        }}
      />
    </LayoutShiftContent>
  ),
  [MyFirstGameStepNames.estherTurnPreFold]: ({ contentLayout, windowDimensions }) => {
    const width = useBreakPointValue({
      default: 330,
      sm: undefined
    }) as number | undefined
    return (
      <LayoutShiftContent hostName="myFirstGame.board" position="bottom">
        <ToolTip
          title={"Esther's Turn"}
          description={
            "Since Esther checked earlier and Petra bet afterward, Esther now needs to match Petra's bet to stay in the hand and see the next community card. She can choose to Raise, Call, or Fold."
          }
          containerStyle={{
            zIndex: 999,
            // marginTop: -120,
            marginTop: !width ? -60 : -120,
            maxWidth: '100%',
            width: width,
            transform: [
              {
                translateX: !width ? 0 : getTranslateXCentered(330, { contentLayout, windowDimensions })
              }
            ]
          }}
        />
      </LayoutShiftContent>
    )
  },
  [MyFirstGameStepNames.handRankingsNote]: ({ contentLayout, windowDimensions }) => {
    const width = Platform.OS === 'android' ? 300 : 330
    const maxWidth = useBreakPointValue<ViewStyle['maxWidth']>({
      default: '100%',
      sm: width
    })

    return (
      <Portal hostName="layoutShiftProvider">
        <LayoutShiftContent hostName="myFirstGame.stepper.handRanking" position="bottom-right">
          <ToolTip
            title={'Hand Ranking Cheat Sheet'}
            description={'Want to learn the best card combinations? Access the hand rankings cheat sheet.'}
            showArrow={true}
            arrowPosition={'topRight'}
            containerStyle={{
              zIndex: 999,
              width,
              maxWidth,
              transform: [
                { translateY: 10 },
                {
                  translateX: width * -1 + 16
                }
              ]
            }}
          />
        </LayoutShiftContent>
      </Portal>
    )
  },
  [MyFirstGameStepNames.beforePetraBets3]: ({ contentLayout, windowDimensions }) => (
    <LayoutShiftContent hostName="myFirstGame.board" position="bottom">
      <ToolTip
        title={'Bet'}
        description={
          'Petra evaluates the Turn card, the 8 of Diamonds, and believes her pair of Queens is still the strongest hand.'
        }
        showArrow={true}
        arrowPosition={'topRight'}
        containerStyle={{
          zIndex: 999,
          marginTop: -70,
          maxWidth: '100%',
          width: 330,
          transform: [
            {
              translateX: getTranslateXCentered(330, { contentLayout, windowDimensions })
            }
          ]
        }}
      />
    </LayoutShiftContent>
  ),
  [MyFirstGameStepNames.userTurnCall3]: ({ contentLayout, windowDimensions }) => (
    <LayoutShiftContent hostName="myFirstGame.playerCards" position="top">
      <ToolTip
        title={'Call'}
        description={
          "Your pair of Jacks remains strong, and one more community card is yet to be revealed. Call Petra's bet to stay in the hand and see the next card."
        }
        showArrow={true}
        showNext={false}
        containerStyle={{
          zIndex: 999,
          marginTop: -130,
          maxWidth: '100%',
          width: 330,
          transform: [
            {
              translateX: getTranslateXCentered(330, { contentLayout, windowDimensions })
            }
          ]
        }}
      />
    </LayoutShiftContent>
  ),
  [MyFirstGameStepNames.userTurnCall4]: ({ contentLayout, windowDimensions }) => (
    <LayoutShiftContent hostName="myFirstGame.playerCards" position="top">
      <ToolTip
        title={'Call the Bet'}
        description={
          "The Jack of Spades on the river strengthens your hand from a pair to three of a kind. It's a solid hand, though Petra’s confidence suggests a strong hand as well. Select Call to match her bet and stay in the game."
        }
        showArrow={true}
        showNext={false}
        containerStyle={{
          zIndex: 999,
          marginTop: -150,
          maxWidth: '100%',
          width: 350,
          transform: [
            {
              translateX: isWeb
                ? (contentLayout?.width ? contentLayout?.width / 2 : 0) - 350 / 2
                : windowDimensions.width / 2 - 350 / 2
            }
          ]
        }}
      />
    </LayoutShiftContent>
  )
}
type OverlayStageComponentProps = {
  contentLayout: MyFirstGameOverlayProps['contentLayout']
  windowDimensions: ReturnType<typeof useWindowDimensions>
  store: ReturnType<typeof useStore>
}
type OverlayStageComponent = (props: OverlayStageComponentProps) => React.ReactElement

const OverlayStageMap: Record<MyFirstGameStepString[number], OverlayStageComponent> = {
  [MyFirstGameStepNames.letsBegin]: () => (
    <MyFirstGameInterstitial
      text={
        <>
          <DS.Text variant={'Heading 1/Medium'} color={'Global/White'}>
            Let’s Begin
          </DS.Text>
          <DS.Text variant={'Subheading/Medium'} color={'Global/White'}>
            Today, we’ll be playing No Limit Texas Hold'em', a popular variation of poker that we love at Poker Power.
          </DS.Text>
          <DS.Text variant={'Subheading/Regular'} color={'Brand/Light Grey'}>
            The objective is to win chips by forming the{' '}
            <DS.Text variant={'Subheading/Medium'} color={'Global/White'}>
              best five-card hand
            </DS.Text>{' '}
            using a combination of your two hole cards and the five community cards, or by making other players fold
            through strategic betting.
          </DS.Text>
        </>
      }
    />
  ),
  [MyFirstGameStepNames.rounds]: () => (
    <MyFirstGameInterstitial
      text={
        <>
          <DS.Text variant={'Heading 1/Medium'} color={'Global/White'}>
            Rounds
          </DS.Text>
          <DS.Text variant={'Subheading/Regular'} color={'Brand/Light Grey'}>
            <DS.Text variant={'Subheading/Medium'} color={'Global/White'}>
              There are four potential rounds: Pre-flop, Flop, Turn, and River.
            </DS.Text>{' '}
            A new round starts once all players have acted and matched the bet or folded. The hand ends if:
          </DS.Text>
          <DS.Frame style={{ flexDirection: 'row', gap: 8 }}>
            <DiamondBulletPoint color={'#FFFFFF'} preserveAspectRatio="xMidYMid meet" style={{ marginTop: 4 }} />
            <DS.Text style={{ flex: 1 }} variant={'Body Large/Medium'} color={'Global/White'}>
              Only one player remains{'\n'}
              <DS.Text variant={'Body Large/Regular'} color={'Brand/Light Grey'}>
                (others have folded)
              </DS.Text>
            </DS.Text>
          </DS.Frame>
          <DS.Frame style={{ flexDirection: 'row', gap: 8 }}>
            <DiamondBulletPoint color={'#FFFFFF'} preserveAspectRatio="xMidYMid meet" style={{ marginTop: 4 }} />
            <DS.Text style={{ flex: 1 }} variant={'Body Large/Medium'} color={'Global/White'}>
              The showdown occurs{'\n'}
              <DS.Text variant={'Body Large/Regular'} color={'Brand/Light Grey'}>
                where players reveal their cards, and a winner is determined.
              </DS.Text>
            </DS.Text>
          </DS.Frame>
        </>
      }
    />
  ),
  [MyFirstGameStepNames.preFlopRoundNote]: () => (
    <MyFirstGameInterstitial
      text={
        <>
          <DS.Text variant={'Heading 1/Medium'} color={'Global/White'}>
            Pre-Flop Round
          </DS.Text>
          <DS.Text variant={'Subheading/Regular'} color={'Brand/Light Grey'}>
            You've been dealt two cards, and you'll need to decide whether to call, raise, or fold. To stay in the hand,
            look at the blind amounts posted by BB & SB—
            <DS.Text variant={'Subheading/Medium'} color={'Global/White'}>
              if you think your hand is strong, you can call by matching the BB or raise by betting more
            </DS.Text>
            , but if your cards aren't good, you can fold and sit out this round.
          </DS.Text>
        </>
      }
    />
  ),
  [MyFirstGameStepNames.theFlop]: ({ store }) => (
    <MyFirstGameInterstitial
      text={
        <>
          <DS.Text variant={'Heading 2/Medium'} color={'Global/White'}>
            The Flop
          </DS.Text>
          <DS.Text variant={'Subheading/Regular'} color={'Brand/Light Grey'}>
            <DS.Text variant={'Subheading/Medium'} color={'Global/White'}>
              The first 3 community cards are revealed on the table.
            </DS.Text>{' '}
            Use them with your hole cards to form the strongest hand. Betting starts with the first active player to the
            left of the dealer.
          </DS.Text>
        </>
      }
      cards={
        <DS.Frame>
          <DS.Frame
            style={{
              flexDirection: 'row',
              gap: 4,
              justifyContent: 'center',
              marginTop: 16 //isSmallDevice ? 16 : 32
            }}
          >
            {store.game.communityCards.map((card, i) => (
              <Card
                key={`board-card-top-${card}-${i}`}
                card={card}
                disabled={false}
                isOnTable
                // fontSize={verticalScale(19)}
                fontSize={19}
              />
            ))}
          </DS.Frame>
          <DS.Frame style={{ flexDirection: 'row', gap: 4, justifyContent: 'center', marginTop: 4 }}>
            {[undefined, undefined].map((card, i) => (
              <Card
                key={`board-card-bottom-${card}-${i}`}
                card={card}
                disabled={false}
                isOnTable
                // fontSize={verticalScale(19)}
                fontSize={19}
              />
            ))}
          </DS.Frame>
        </DS.Frame>
      }
    />
  ),
  [MyFirstGameStepNames.theTurn]: ({ store }) => (
    <MyFirstGameInterstitial
      text={
        <>
          <DS.Text variant={'Heading 2/Medium'} color={'Global/White'}>
            The Turn Round
          </DS.Text>
          <DS.Text variant={'Subheading/Regular'} color={'Brand/Light Grey'}>
            <DS.Text variant={'Subheading/Medium'} color={'Global/White'}>
              A fourth card has been dealt on the table.
            </DS.Text>{' '}
            Evaluate your hand with the new card to see if it improves your chances, or if you think other players might
            have a stronger hand based on their actions.
          </DS.Text>
        </>
      }
      cards={
        <DS.Frame>
          <DS.Frame
            style={{
              flexDirection: 'row',
              gap: 4,
              justifyContent: 'center',
              marginTop: 16 //isSmallDevice ? 16 : 32
            }}
          >
            {store.game.communityCards.slice(0, 3).map((card, i) => (
              <Card
                key={`board-card-${card}-${i}`}
                card={card}
                disabled={true}
                animate={false}
                isOnTable
                // fontSize={verticalScale(19)}
                fontSize={19}
              />
            ))}
          </DS.Frame>
          <DS.Frame style={{ flexDirection: 'row', gap: 4, justifyContent: 'center', marginTop: 4 }}>
            {[...store.game.communityCards.slice(3, 4), undefined].map((card, i) => (
              <Card
                key={`board-card-${card}-${i}`}
                card={card}
                disabled={false}
                isOnTable
                // fontSize={verticalScale(19)}
                fontSize={19}
              />
            ))}
          </DS.Frame>
        </DS.Frame>
      }
    />
  ),
  [MyFirstGameStepNames.theRiver]: ({ store }) => (
    <MyFirstGameInterstitial
      text={
        <>
          <DS.Text variant={'Heading 1/Medium'} color={'Global/White'}>
            The River Round
          </DS.Text>
          <DS.Text variant={'Subheading/Regular'} color={'Brand/Light Grey'}>
            <DS.Text variant={'Subheading/Medium'} color={'Global/White'}>
              The final community card
            </DS.Text>{' '}
            has been dealt, giving you one last chance to improve your hand. Now, you’ll need to{' '}
            <DS.Text variant={'Subheading/Medium'} color={'Global/White'}>
              decide whether to bet, call, raise, or fold
            </DS.Text>{' '}
            before showdown.
          </DS.Text>
        </>
      }
      cards={
        <DS.Frame>
          <DS.Frame
            style={{
              flexDirection: 'row',
              gap: 4,
              justifyContent: 'center',
              marginTop: 16 //isSmallDevice ? 16 : 32
            }}
          >
            {store.game.communityCards.slice(0, 3).map((card, i) => (
              <Card
                key={`board-card-${card}-${i}`}
                card={card}
                disabled={true}
                animate={false}
                isOnTable
                // fontSize={verticalScale(19)}
                fontSize={19}
              />
            ))}
          </DS.Frame>
          <DS.Frame style={{ flexDirection: 'row', gap: 4, justifyContent: 'center', marginTop: 4 }}>
            {store.game.communityCards.slice(3, 5).map((card, i) => (
              <Card
                key={`board-card-${card}-${i}`}
                card={card}
                disabled={i === 0}
                animate={i === 1}
                isOnTable
                // fontSize={verticalScale(19)}
                fontSize={19}
              />
            ))}
          </DS.Frame>
        </DS.Frame>
      }
    />
  ),
  [MyFirstGameStepNames.theShowdown]: () => (
    <MyFirstGameInterstitial
      text={
        <>
          <DS.Text variant={'Heading 1/Medium'} color={'Global/White'}>
            The Showdown
          </DS.Text>
          <DS.Text
            variant={'Subheading/Regular'}
            style={{ fontFamily: fontFamily.GTAmericaStandardRegular }}
            color={'Brand/Light Grey'}
          >
            For teaching purposes, everyone’s hole cards are already face up, but typically, in a showdown, the hole
            cards of the remaining players are revealed to determine the strongest hand.{' '}
            <DS.Text
              variant={'Subheading/Medium'}
              color={'Global/White'}
              style={{ fontFamily: fontFamily.GTAmericaStandardMedium }}
            >
              The player with the best hand wins the pot.
            </DS.Text>
          </DS.Text>
        </>
      }
    />
  )
}

export default MyFirstGameOverlay
